import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useOutletContext } from "react-router-dom";
import { Pagination } from "@mui/material";
import sharedStyles from "../../styles/SharedLayout.module.css";
import styles from "./SearchResults.module.css";
import Filters from "../../components/Filters/Filters";
import ProductGrid from "../../components/ProductGrid/ProductGrid";
import { searchProducts, type SortOption } from "../../utils/meilisearchClient";
import { extractFiltersFromProducts } from "../../utils/filterUtils";
import type { MeilisearchProduct } from "../../types-var/product";
import SearchResultsSkeleton from "./components/SearchResultsSkeleton/SearchResultsSkeleton";
import SearchSEO from "../../components/SEO/SearchSEO"; // Import the new SearchSEO component
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const SearchResults: React.FC = () => {
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<MeilisearchProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableFilters, setAvailableFilters] = useState<{
    [key: string]: string[];
  }>({});
  const [filterStyles, setFilterStyles] = useState<{ [key: string]: string }>(
    {}
  );
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [totalHits, setTotalHits] = useState(0);
  const [filterCounts, setFilterCounts] = useState<{
    [key: string]: { [value: string]: number };
  }>({});

  const { toggleMiniCart } = useOutletContext<{ toggleMiniCart: () => void }>();

  const query = searchParams.get("q") || "";
  const currentSort = (searchParams.get("sort") ||
    "popularity:desc") as SortOption;
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const HITS_PER_PAGE = 24;

  const [localPage, setLocalPage] = useState(currentPage);

  useEffect(() => {
    setLocalPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (query !== "" || currentSort !== "popularity:desc") {
      const url = new URL(window.location.href);
      url.searchParams.set("page", "1");
      window.history.replaceState({}, "", url.toString());
      setSearchParams(new URLSearchParams(url.search));
      setLocalPage(1);
    }
  }, [currentSort, query, setSearchParams]);

  useEffect(() => {
    setFilters({});
    setAvailableFilters({});
  }, [query]);

  const fetchResults = useCallback(
    async (currentFilters: typeof filters) => {
      try {
        const searchOptions: any = {
          sort: [currentSort],
          page: localPage,
          hitsPerPage: HITS_PER_PAGE,
        };

        console.log(`Fetching search results for page: ${localPage}`);

        let filterConditions: string[] = [];

        if (Object.keys(currentFilters).length > 0) {
          Object.entries(currentFilters)
            .filter(([_, values]) => values.length > 0)
            .forEach(([key, values]) => {
              if (key === "Categorías") {
                const categoryFilters = values.map(
                  (categoryId) => `categories.id = ${categoryId}`
                );
                if (categoryFilters.length > 0) {
                  filterConditions.push(`(${categoryFilters.join(" OR ")})`);
                }
              } else {
                const attributeFilters = values.map((value) => {
                  try {
                    const parsed = JSON.parse(value);
                    return `(attributes.name = "${key}" AND attributes.values.name = "${parsed.name}")`;
                  } catch {
                    const safeValue = value.replace(/"/g, '\\"');
                    return `(attributes.name = "${key}" AND attributes.values.name = "${safeValue}")`;
                  }
                });
                if (attributeFilters.length > 0) {
                  filterConditions.push(`(${attributeFilters.join(" OR ")})`);
                }
              }
            });

          if (filterConditions.length > 0) {
            searchOptions.filter = filterConditions.join(" AND ");
          }
        }

        const results = await searchProducts(query, searchOptions);
        return results;
      } catch (error) {
        console.error("Search failed:", error);
        throw error;
      }
    },
    [currentSort, localPage, query]
  );

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  useEffect(() => {
    const updateResults = async () => {
      setLoading(true);
      try {
        const results = await fetchResults(filters);
        const {
          filters: extractedFilters,
          styles,
          counts,
        } = extractFiltersFromProducts(results.hits as MeilisearchProduct[]);

        setProducts(results.hits as MeilisearchProduct[]);
        setAvailableFilters(extractedFilters);
        setFilterStyles(styles);
        setFilterCounts(counts);
        setTotalPages(results.totalPages);
        setTotalHits(results.totalHits || 0);
      } catch (error) {
        setError("Failed to load search results. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    updateResults();
  }, [query, currentSort, localPage, filters, fetchResults]);

  const handleSortChange = (sortBy: SortOption) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("sort", sortBy);
    newSearchParams.set("page", "1");
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    if (_event) {
      _event.preventDefault();
    }

    setLocalPage(page);

    console.log(`Setting page to ${page}, updating URL and fetching new data`);

    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    window.history.pushState({}, "", url.toString());
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    console.log(`Current page state changed to: ${currentPage}`);
  }, [currentPage]);

  if (loading) {
    return <SearchResultsSkeleton />;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!loading && products.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyStateTitle}>Sin resultados para "{query}"</p>
        <p className={styles.emptyStateMessage}>
          Intenta con diferentes palabras clave o elimina algunos filtros
        </p>

        <div className={styles.whatsappContainer}>
          <div className={styles.whatsappContent}>
            <WhatsAppIcon className={styles.whatsappIcon} />
            <div className={styles.whatsappText}>
              <h3>¿No encuentras lo que buscas?</h3>
              <p>
                ¡Podemos ayudarte! Pregúntanos directamente por WhatsApp y
                haremos lo posible por conseguir el producto que necesitas.
              </p>
            </div>
          </div>
          <a
            href="https://wa.me/51906869576?text=Hola,%20estoy%20buscando%20un%20producto%20que%20no%20encuentro%20en%20la%20web:%20"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.whatsappButton}
          >
            Contactar por WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add SearchSEO component */}
      <SearchSEO query={query} totalResults={totalHits} />

      <div className="container">
        <div className={sharedStyles.pageContainer}>
          <h1 className={sharedStyles.pageTitle}>Resultados para "{query}"</h1>

          <div className={sharedStyles.mainContent}>
            <div className={sharedStyles.filtersColumn}>
              {/* Only show Filters in desktop view */}
              <div className={styles.desktopFiltersOnly}>
                <Filters
                  availableFilters={availableFilters}
                  filters={filters}
                  filterStyles={filterStyles}
                  setFilters={handleFilterChange}
                  filterCounts={filterCounts}
                  isMobile={false}
                />
              </div>
            </div>

            <div className={sharedStyles.productsColumn}>
              <ProductGrid
                products={products}
                toggleMiniCart={toggleMiniCart}
                onSortChange={handleSortChange}
                currentSort={currentSort}
                filterStyles={filterStyles}
                availableFilters={availableFilters}
                filterCounts={filterCounts}
                totalHits={totalHits}
                query={query}
                filters={filters}
                setFilters={handleFilterChange}
                onClearFilters={() => handleFilterChange({})}
              />
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <Pagination
                    count={totalPages}
                    page={localPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    className="customPagination"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                  />
                  <div
                    style={{
                      marginTop: "10px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Pag {localPage} de {totalPages}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResults;
